import { useState, type ReactNode } from 'react';
import { IconTile } from '@/components/dashboard/IconTile';
import {
  ATTENTION,
  DAILY,
  EARNINGS,
  KPIS,
  PERIOD_LABEL,
  TRANSACTIONS,
  VENDORS,
} from '@/constants/merchantDashboardData';

const inr = (n: number) => `₹${n.toLocaleString('en-IN')}`;
const pct = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0);

function Card({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="card shadow-kiosk-sm h-100">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="text-headline-sm text-on-surface mb-0">{title}</h3>
          {action}
        </div>
        {children}
      </div>
    </div>
  );
}

const Dot = ({ className }: { className: string }) => (
  <span className={`d-inline-block rounded-circle flex-shrink-0 ${className}`} style={{ width: 10, height: 10 }} />
);

// ── Title + period filter ────────────────────────────────────────────────
const PERIODS = ['Today', '7 days', '30 days'];

export function PageHeader() {
  const [period, setPeriod] = useState('7 days');
  return (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
      <div>
        <h2 className="text-headline-lg text-on-surface mb-1">Dashboard</h2>
        <p className="text-body-md text-on-surface-variant mb-0">
          Your vendors and kiosks at a glance · {PERIOD_LABEL.toLowerCase()} · sample data
        </p>
      </div>
      <div className="btn-group" role="group" aria-label="Report period">
        {PERIODS.map((p) => (
          <button
            key={p}
            type="button"
            className={`btn btn-sm ${period === p ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setPeriod(p)}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── KPI cards ────────────────────────────────────────────────────────────
export function KpiRow() {
  return (
    <div className="row g-3">
      {KPIS.map((k) => (
        <div key={k.key} className="col-12 col-sm-6 col-xl-3">
          <div className="card shadow-kiosk-sm h-100">
            <div className="card-body d-flex align-items-center gap-3">
              <IconTile icon={k.icon} />
              <div className="flex-grow-1">
                <div className="text-label-md text-on-surface-variant">{k.label}</div>
                <div className="d-flex align-items-baseline gap-2 flex-wrap">
                  <span className="text-headline-lg text-on-surface">{k.value}</span>
                  <span className={`badge rounded-pill ${k.up ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'}`}>
                    {k.delta}
                  </span>
                </div>
                <div className="text-body-sm text-on-surface-variant">{k.note}</div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Revenue chart (vendor share + commission) ────────────────────────────
export function RevenueChart() {
  const max = Math.max(...DAILY.map((d) => d.revenue));
  return (
    <Card
      title="Revenue"
      action={
        <span className="d-flex align-items-center gap-3 text-label-sm text-on-surface-variant">
          <span className="d-flex align-items-center gap-1"><Dot className="bg-primary" /> Vendor share</span>
          <span className="d-flex align-items-center gap-1"><Dot className="bg-secondary" /> Your commission</span>
        </span>
      }
    >
      <div className="d-flex align-items-end gap-3" style={{ height: 230 }}>
        {DAILY.map((d) => (
          <div
            key={d.day}
            className="flex-grow-1 d-flex flex-column justify-content-end align-items-center h-100"
            title={`${d.day} ${d.date}: ${inr(d.revenue)} (commission ${inr(d.commission)})`}
          >
            <span className="text-label-sm text-on-surface-variant mb-1">{inr(d.revenue)}</span>
            <div className="w-100 d-flex flex-column rounded-top-3 overflow-hidden" style={{ height: `${(d.revenue / max) * 78}%` }}>
              <div className="bg-secondary" style={{ height: `${(d.commission / d.revenue) * 100}%` }} />
              <div className="bg-primary flex-grow-1" />
            </div>
            <span className="text-label-sm text-on-surface-variant mt-2">{d.day}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Earnings split ───────────────────────────────────────────────────────
export function EarningsSplit() {
  const rows = [
    { label: 'Vendor share', value: EARNINGS.vendorShare, tone: 'bg-primary' },
    { label: 'Your commission', value: EARNINGS.commission, tone: 'bg-secondary' },
    { label: 'Convenience fees', value: EARNINGS.convenienceFees, tone: 'bg-warning' },
  ];
  return (
    <Card title="Where the money goes">
      <div className="progress-stacked mb-4" style={{ height: 12 }}>
        {rows.map((r) => (
          <div key={r.label} className="progress" style={{ width: `${pct(r.value, EARNINGS.gross)}%` }}>
            <div className={`progress-bar ${r.tone}`} />
          </div>
        ))}
      </div>
      <div className="d-flex flex-column gap-3">
        {rows.map((r) => (
          <div key={r.label} className="d-flex align-items-center gap-2">
            <Dot className={r.tone} />
            <span className="text-label-md text-on-surface flex-grow-1">{r.label}</span>
            <span className="text-label-md text-on-surface">{inr(r.value)}</span>
            <span className="text-body-sm text-on-surface-variant text-end" style={{ minWidth: 44 }}>
              {pct(r.value, EARNINGS.gross)}%
            </span>
          </div>
        ))}
        <div className="d-flex align-items-center gap-2 border-top pt-3">
          <span className="text-label-md text-on-surface flex-grow-1">Total</span>
          <span className="text-label-md text-on-surface">{inr(EARNINGS.gross)}</span>
          <span style={{ minWidth: 44 }} />
        </div>
      </div>
    </Card>
  );
}

// ── Vendors ──────────────────────────────────────────────────────────────
// Round chart: each vendor's share of revenue, with their numbers beside it
const VENDOR_TONES = [
  { dot: 'bg-primary', color: 'var(--bs-primary, #00129a)' },
  { dot: 'bg-secondary', color: 'var(--bs-secondary, #525c8a)' },
  { dot: 'bg-success', color: 'var(--bs-success, #198754)' },
];
const vendorTone = (i: number) => VENDOR_TONES[i % VENDOR_TONES.length] ?? VENDOR_TONES[0]!;

export function VendorTable() {
  const total = VENDORS.reduce((s, v) => s + v.revenue, 0);
  let acc = 0;
  const stops = VENDORS.map((v, i) => {
    const from = acc;
    acc += pct(v.revenue, total);
    return `${vendorTone(i).color} ${from}% ${acc}%`;
  }).join(', ');

  return (
    <Card title="Vendors" action={<span className="text-label-md text-primary">View all</span>}>
      <div className="d-flex flex-wrap align-items-center gap-4">
        <div
          className="position-relative flex-shrink-0"
          style={{ width: 160, height: 160, borderRadius: '50%', background: `conic-gradient(${stops})` }}
          role="img"
          aria-label="Revenue share by vendor"
        >
          <div
            className="position-absolute top-50 start-50 translate-middle bg-surface-lowest rounded-circle d-flex flex-column align-items-center justify-content-center"
            style={{ width: 104, height: 104 }}
          >
            <span className="text-headline-sm text-on-surface">{inr(total)}</span>
            <span className="text-label-sm text-on-surface-variant">{VENDORS.length} vendors</span>
          </div>
        </div>

        <div className="d-flex flex-column gap-3 flex-grow-1">
          {VENDORS.map((v, i) => (
            <div key={v.name}>
              <div className="d-flex align-items-center gap-2">
                <Dot className={vendorTone(i).dot} />
                <span className="text-label-md text-on-surface flex-grow-1">{v.name}</span>
                <span className="text-label-md text-on-surface">{inr(v.revenue)}</span>
                <span className="text-body-sm text-on-surface-variant text-end" style={{ minWidth: 44 }}>
                  {Math.round(pct(v.revenue, total))}%
                </span>
              </div>
              <div className="text-body-sm text-on-surface-variant ms-4">
                {v.jobs.toLocaleString('en-IN')} jobs · {inr(v.commission)} commission ·{' '}
                <span className={v.online < v.kiosks ? 'text-warning fw-semibold' : ''}>
                  {v.online}/{v.kiosks} kiosks online
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ── Needs attention ──────────────────────────────────────────────────────
const LEVEL_ICON: Record<string, string> = {
  danger: 'bi-x-octagon-fill text-danger',
  warning: 'bi-exclamation-triangle-fill text-warning',
  info: 'bi-info-circle-fill text-primary',
};

export function NeedsAttention() {
  return (
    <Card title="Needs attention">
      <div className="d-flex flex-column gap-2">
        {ATTENTION.map((a) => (
          <div key={a.title} className="d-flex align-items-center gap-3 bg-surface-low rounded-3 px-3 py-2">
            <i className={`bi ${LEVEL_ICON[a.level]}`} />
            <div className="flex-grow-1">
              <div className="text-label-md text-on-surface">{a.title}</div>
              <div className="text-body-sm text-on-surface-variant">
                {a.kiosk} · {a.time}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Recent transactions ──────────────────────────────────────────────────
const STATUS_BADGE: Record<string, string> = {
  Success: 'bg-success-subtle text-success',
  Pending: 'bg-warning-subtle text-warning',
  Failed: 'bg-danger-subtle text-danger',
};

export function RecentTransactions() {
  return (
    <Card title="Recent transactions" action={<span className="text-label-md text-primary">View all</span>}>
      <div className="table-responsive">
        <table className="table align-middle mb-0">
          <thead>
            <tr className="text-label-sm text-on-surface-variant">
              <th className="bg-surface-low border-0 rounded-start-3">Transaction</th>
              <th className="bg-surface-low border-0">Kiosk</th>
              <th className="bg-surface-low border-0">Mode</th>
              <th className="bg-surface-low border-0 text-end">Amount</th>
              <th className="bg-surface-low border-0 text-end">Commission</th>
              <th className="bg-surface-low border-0">Status</th>
              <th className="bg-surface-low border-0 rounded-end-3">Time</th>
            </tr>
          </thead>
          <tbody>
            {TRANSACTIONS.map((t) => (
              <tr key={t.id} className="text-body-sm text-on-surface">
                <td className="fw-semibold">{t.id}</td>
                <td>{t.kiosk}</td>
                <td>{t.mode}</td>
                <td className="text-end">{inr(t.amount)}</td>
                <td className="text-end">{inr(t.commission)}</td>
                <td>
                  <span className={`badge rounded-pill ${STATUS_BADGE[t.status]}`}>{t.status}</span>
                </td>
                <td className="text-on-surface-variant">{t.time}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
